/**
 * BDM "Team leads" page (G2.2).
 *
 * Renders for a BDM (or admin viewing any user). Pulls one of:
 *   /api/zoho/team-leads             — signed-in BDM's own team
 *   /api/zoho/users/:id/leads        — admin impersonation
 *
 * The two endpoints return the same shape (`{ ok, leads, team, viewingAs }`),
 * so this component switches mode based on the `viewAs` prop.
 */
import { useEffect, useMemo, useState } from 'react';
import {
    RefreshCw, Search, AlertTriangle, Phone, Mail, ExternalLink,
    Users as UsersIcon, TrendingUp, CheckCircle2, Layers, Filter,
} from 'lucide-react';
import api from '../../utils/api';

const STATUS_CHIPS = {
    'Not Contacted': 'bg-amber-100 text-amber-800',
    'Contacted': 'bg-sky-100 text-sky-800',
    'Pre-Qualified': 'bg-violet-100 text-violet-800',
    'Qualified': 'bg-emerald-100 text-emerald-800',
    'Lost Lead': 'bg-rose-100 text-rose-800',
    'Junk Lead': 'bg-ink-200 text-ink-600',
};
const LOST_STATUSES = new Set(['Lost Lead', 'Junk Lead']);
const WON_STATUSES  = new Set(['Qualified', 'Closed Won', 'Won']);

/**
 * @param {object} props
 * @param {string} [props.viewAs]    — pass a userId to render the admin impersonation view.
 * @param {boolean} [props.embedded] — when true, skips the page-level header.
 */
export default function TeamLeads({ viewAs, embedded = false }) {
    const [data, setData]     = useState(null);
    const [loading, setLoading] = useState(false);
    const [q, setQ]           = useState('');
    const [memberFilter, setMemberFilter] = useState('all');

    const endpoint = viewAs ? `/zoho/users/${viewAs}/leads` : '/zoho/team-leads';

    const load = (fresh = false) => {
        setLoading(true);
        api.get(endpoint, { params: { fresh: fresh ? 'true' : '' } })
            .then(r => setData(r.data))
            .catch(() => setData({ ok: false }))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(false); /* eslint-disable-next-line */ }, [endpoint]);

    const teamRoster = useMemo(() => {
        if (!data) return [];
        const team = data.team || [];
        if (viewAs && data.viewingAs) return [data.viewingAs, ...team];
        return team;
    }, [data, viewAs]);

    const ownerLabel = useMemo(() => {
        const byId    = new Map();
        const byEmail = new Map();
        for (const u of teamRoster) {
            if (u.zohoUserId) byId.set(String(u.zohoUserId), u.name);
            if (u.email)      byEmail.set(String(u.email).toLowerCase(), u.name);
        }
        return (lead) => byId.get(lead.ownerId) || byEmail.get(lead.ownerEmail) || lead.owner || '—';
    }, [teamRoster]);

    const rows = useMemo(() => {
        const all = data?.leads || [];
        const rx  = q.trim() ? new RegExp(q.trim(), 'i') : null;
        return all.filter(l => {
            if (memberFilter !== 'all') {
                const member  = teamRoster.find(u => String(u._id) === memberFilter);
                const matchId = member?.zohoUserId && String(member.zohoUserId) === l.ownerId;
                const matchEm = member?.email && String(member.email).toLowerCase() === l.ownerEmail;
                if (!matchId && !matchEm) return false;
            }
            if (rx) {
                const blob = `${l.firstName} ${l.lastName} ${l.email} ${l.phone} ${l.company} ${ownerLabel(l)}`;
                if (!rx.test(blob)) return false;
            }
            return true;
        });
    }, [data, q, memberFilter, teamRoster, ownerLabel]);

    const kpis = useMemo(() => {
        const all = data?.leads || [];
        const k   = { total: all.length, active: 0, won: 0, lost: 0 };
        for (const l of all) {
            const s = l.status;
            if (LOST_STATUSES.has(s)) k.lost += 1;
            else if (WON_STATUSES.has(s)) k.won += 1;
            else k.active += 1;
        }
        const closed = k.won + k.lost;
        k.conversion = closed > 0 ? Math.round((k.won / closed) * 100) : 0;
        return k;
    }, [data]);

    const stageMix = useMemo(() => {
        const buckets = {};
        for (const l of (data?.leads || [])) {
            const s = l.status || 'Unknown';
            buckets[s] = (buckets[s] || 0) + 1;
        }
        return Object.entries(buckets).sort((a, b) => b[1] - a[1]);
    }, [data]);

    return (
        <div className="space-y-4">
            {!embedded && (
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-2xl font-extrabold">
                            {viewAs ? (data?.viewingAs?.name ? `${data.viewingAs.name}'s leads` : 'User leads') : 'Team leads'}
                        </h1>
                        <p className="text-sm text-ink-500">
                            {viewAs
                                ? 'Admin view — these are the leads this user sees on their own dashboard.'
                                : 'Leads owned by you and every BDA / BDE reporting to you.'}
                        </p>
                    </div>
                    <button onClick={() => load(true)} className="ag-btn-ghost" disabled={loading}>
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                </div>
            )}

            {data?.scoped === 'no-owner-keys' && (
                <div className="ag-card p-6 bg-amber-50 ring-1 ring-amber-200 text-sm">
                    <div className="inline-flex items-start gap-2 text-amber-800">
                        <AlertTriangle className="h-4 w-4 mt-0.5" />
                        <div>
                            <b>No team members have Zoho identity yet.</b>
                            <p className="text-[12px] mt-1">{data.hint}</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi icon={Layers}       label="Total leads"  value={kpis.total} />
                <Kpi icon={UsersIcon}    label="Active"       value={kpis.active}          sub="open + in-progress" />
                <Kpi icon={CheckCircle2} label="Won"          value={kpis.won}             sub="qualified / closed-won" />
                <Kpi icon={TrendingUp}   label="Conversion"   value={`${kpis.conversion}%`} sub="won / (won + lost)" />
            </div>

            {stageMix.length > 0 && (
                <div className="ag-card p-4">
                    <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-2">Stage distribution</div>
                    <div className="flex h-3 rounded-full overflow-hidden bg-ink-100">
                        {stageMix.map(([status, n]) => (
                            <div key={status} title={`${status} · ${n}`}
                                style={{ width: `${(n / kpis.total) * 100}%` }}
                                className={`h-full ${LOST_STATUSES.has(status) ? 'bg-rose-400'
                                    : WON_STATUSES.has(status) ? 'bg-emerald-500'
                                    : status === 'Contacted' ? 'bg-sky-500'
                                    : status === 'Pre-Qualified' ? 'bg-violet-500'
                                    : 'bg-amber-400'}`}
                            />
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {stageMix.map(([status, n]) => (
                            <span key={status} className={`ag-chip ${STATUS_CHIPS[status] || 'bg-ink-100 text-ink-700'}`}>
                                {status} · {n}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {teamRoster.length > 0 && (
                <div className="ag-card p-3 flex items-center gap-2 flex-wrap">
                    <Filter className="h-3.5 w-3.5 text-ink-500" />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Filter by team member:</span>
                    <button
                        onClick={() => setMemberFilter('all')}
                        className={`text-[11px] px-2.5 py-1 rounded-full ${memberFilter === 'all' ? 'bg-brand-teal text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}
                    >
                        All ({data?.leads?.length || 0})
                    </button>
                    {teamRoster.map(u => (
                        <button key={u._id}
                            onClick={() => setMemberFilter(String(u._id))}
                            className={`text-[11px] px-2.5 py-1 rounded-full inline-flex items-center gap-1
                                ${memberFilter === String(u._id) ? 'bg-brand-teal text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}
                            title={u.email}
                        >
                            {u.name}{u.salesDesignation && ` · ${u.salesDesignation.toUpperCase()}`}
                        </button>
                    ))}
                </div>
            )}

            <div className="ag-card p-3 flex items-center gap-2">
                <Search className="h-4 w-4 text-ink-500" />
                <input
                    className="ag-input flex-1"
                    placeholder="Search name, phone, email, company, owner…"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                />
                <span className="text-xs text-ink-500">{rows.length} of {data?.leads?.length || 0}</span>
                {data?.cached && <span className="text-[10px] text-ink-500 italic">cached {Math.round((data.age || 0) / 1000)}s ago</span>}
            </div>

            {rows.length === 0 && !loading
                ? <div className="ag-card p-8 text-center text-sm text-ink-500">No leads in this view yet.</div>
                : (
                    <div className="ag-card p-0 overflow-x-auto">
                        <table className="ag-table">
                            <thead><tr>
                                <th>Name</th><th>Owner</th><th>Company</th>
                                <th>Email / phone</th><th>Source</th><th>Status</th>
                                <th>Created</th><th></th>
                            </tr></thead>
                            <tbody>
                                {rows.map(l => (
                                    <tr key={l.id}>
                                        <td className="font-medium">{l.fullName || '—'}</td>
                                        <td className="text-xs text-ink-700 font-semibold">{ownerLabel(l)}</td>
                                        <td className="text-xs">{l.company || '—'}</td>
                                        <td>
                                            {l.email && <div className="text-xs inline-flex items-center gap-1"><Mail className="h-3 w-3 text-ink-500" /> {l.email}</div>}
                                            {l.phone && <div className="text-[11px] inline-flex items-center gap-1 text-ink-500 mt-0.5"><Phone className="h-3 w-3" /> {l.phone}</div>}
                                        </td>
                                        <td className="text-xs">{l.source || '—'}</td>
                                        <td>
                                            <span className={`ag-chip ${STATUS_CHIPS[l.status] || 'bg-ink-100 text-ink-700'}`}>{l.status || '—'}</span>
                                        </td>
                                        <td className="text-xs text-ink-500">
                                            {l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN') : '—'}
                                        </td>
                                        <td className="text-right">
                                            <a href={`https://crm.zoho.in/crm/EntityInfo.do?module=Leads&id=${l.id}`}
                                                target="_blank" rel="noreferrer"
                                                className="ag-btn-ghost text-xs" title="Open in Zoho">
                                                Zoho <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            }
        </div>
    );
}

function Kpi({ icon: Icon, label, value, sub }) {
    return (
        <div className="ag-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold inline-flex items-center gap-1">
                <Icon className="h-3 w-3" /> {label}
            </div>
            <div className="text-xl font-extrabold mt-0.5 tabular-nums truncate">{value}</div>
            {sub && <div className="text-[10px] text-ink-500 mt-0.5">{sub}</div>}
        </div>
    );
}
